import { linkOptions, useLocation } from '@tanstack/react-router';

import { RequestForQuoteViewFilterMessage } from '../../../../types/RequestForQuoteViewFilterMessage';
import { RequestForQuoteViewGatewayMessage } from '../../../../types/RequestForQuoteViewGatewayMessage';
import { CenteredPagination } from '../../../cdl/Pagination/CenteredPagination';
import { TableBuilder } from '../../../cdl/TableBuilder/TableBuilder';
import { TableBuilderColumn } from '../../../cdl/TableBuilder/TableBuilderColumn';
import { Tabs } from '../../../cdl/Tabs/Tabs';
import { AdvancedError } from '../../../common/Error/AdvancedError';
import { formatCompanyName } from '../../../common/helpers/formatCompanyName.helper';
import { formatDate } from '../../../common/helpers/formatDate.helper';
import { formatNumber } from '../../../common/helpers/formatNumber.helper';
import { formatVessel } from '../../../common/helpers/formatVessel.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { Characters } from '../../../common/helpers/unicode.helper';
import { useRole } from '../../../common/hooks/useRole';
import { IconListDetails } from '../../../common/icons/cdl/ListDetails';
import { LoadingIndicator } from '../../../common/LoadingIndicator/LoadingIndicator';
import { OfferStatusTag } from '../../../common/OfferStatusTag/OfferStatusTag';
import { TableEmptyState } from '../../../common/TableEmptyState/TableEmptyState';
import { Box } from '../../../common/ui/Box';
import { UnreadIndicator } from '../../../order/common/UnreadIndicator';
import { Port } from '../../../port/Port';
import { useRfqPagination } from '../hooks/useRfqPagination';

import { RfqVendorReferenceDisplay } from './RfqVendorReferenceDisplay';

interface RequestForQuoteViewTableFilters extends RequestForQuoteViewFilterMessage {
    states: string[];
    page: number;
}

interface RequestForQuoteViewTableProps {
    filters: RequestForQuoteViewTableFilters;
    selectedTab: string;
    rfqTotals: Record<string, number>;
    onTabSelect: (tab: string) => void;
    onPageChange: (page: number) => void;
}

export const RfqTable = ({
    filters,
    selectedTab,
    rfqTotals,
    onTabSelect,
    onPageChange,
}: RequestForQuoteViewTableProps) => {
    const role = useRole();
    const location = useLocation();

    const rfqPaginationQuery = useRfqPagination(filters);

    if (rfqPaginationQuery.isPending) {
        return (
            <div>
                <Tabs selectedContext={selectedTab} onSelect={onTabSelect} totals={rfqTotals} />
                <LoadingIndicator />
            </div>
        );
    }

    if (rfqPaginationQuery.isError) {
        return (
            <div>
                <Tabs selectedContext={selectedTab} onSelect={onTabSelect} totals={rfqTotals} />
                <AdvancedError />
            </div>
        );
    }

    if (!rfqPaginationQuery.data?.content.length) {
        return (
            <div>
                <Tabs selectedContext={selectedTab} onSelect={onTabSelect} totals={rfqTotals} />
                <TableEmptyState Icon={IconListDetails} text={translate('order.emptylist')} />
            </div>
        );
    }

    return (
        <div>
            <Tabs selectedContext={selectedTab} onSelect={onTabSelect} totals={rfqTotals} />

            <TableBuilder
                data={rfqPaginationQuery.data.content}
                rowLink={(rfq) => {
                    if (rfq.chosenOfferId) {
                        return linkOptions({
                            to: '/offer/$id',
                            params: { id: rfq.chosenOfferId },
                            state: { originLocation: location },
                        });
                    } else {
                        return linkOptions({
                            to: '/requests-for-quotes/$id',
                            params: { id: rfq.id },
                            state: { originLocation: location },
                        });
                    }
                }}
                isLoading={rfqPaginationQuery.isPlaceholderData && rfqPaginationQuery.isFetching}
                grow={false}
            >
                {role.isSupplier() ? (
                    <TableBuilderColumn<RequestForQuoteViewGatewayMessage> header="" width="1%" hideOnLoading>
                        {(rfq) => <Box width="8px">{!rfq.supplierRead ? <UnreadIndicator /> : null}</Box>}
                    </TableBuilderColumn>
                ) : null}

                <TableBuilderColumn<RequestForQuoteViewGatewayMessage>
                    header={translate('order.vendorreferenceshort')}
                    width="10%"
                    truncate
                >
                    {(rfq) => (
                        <RfqVendorReferenceDisplay
                            offerIds={rfq.offerIds}
                            vendorReferences={rfq.vendorReferences}
                            chosenOfferVendorReference={rfq.chosenOfferVendorReference}
                        />
                    )}
                </TableBuilderColumn>

                {role.isSuperAdmin() || !role.isOneCompanyUser() ? (
                    <TableBuilderColumn<RequestForQuoteViewGatewayMessage>
                        header={role.isSupplier() ? translate('offer.company') : translate('offer.supplier')}
                        width="10%"
                        truncate
                    >
                        {(rfq) => (rfq.supplier ? formatCompanyName({ company: rfq.supplier }) : Characters.EN_DASH)}
                    </TableBuilderColumn>
                ) : null}

                <TableBuilderColumn<RequestForQuoteViewGatewayMessage>
                    header={translate('offer.customer')}
                    width="10%"
                    truncate
                >
                    {(rfq) => (rfq.customer ? formatCompanyName({ company: rfq.customer }) : Characters.EN_DASH)}
                </TableBuilderColumn>

                <TableBuilderColumn<RequestForQuoteViewGatewayMessage>
                    header={translate('order.vessel')}
                    width="15%"
                    truncate
                >
                    {(rfq) => (rfq.vessel ? formatVessel({ vessel: rfq.vessel, short: true }) : Characters.EN_DASH)}
                </TableBuilderColumn>

                <TableBuilderColumn<RequestForQuoteViewGatewayMessage> header={translate('order.port')} width="10%">
                    {(rfq) => {
                        if (!rfq.port) {
                            return null;
                        }

                        return <Port port={rfq.port} vesselId={rfq.vesselId} locode={false} showTooltip />;
                    }}
                </TableBuilderColumn>

                <TableBuilderColumn<RequestForQuoteViewGatewayMessage>
                    header={translate('order.dateDeliveryShort')}
                    width="10%"
                >
                    {(rfq) => {
                        if (!rfq.dateDelivery) {
                            return null;
                        }

                        return formatDate({
                            date: rfq.dateDelivery,
                            timeZone: 'UTC',
                        });
                    }}
                </TableBuilderColumn>

                <TableBuilderColumn<RequestForQuoteViewGatewayMessage> header={translate('order.volume')} width="5%">
                    {(rfq) => (rfq.volume ? `${formatNumber({ number: rfq.volume })}L` : Characters.EN_DASH)}
                </TableBuilderColumn>

                <TableBuilderColumn<RequestForQuoteViewGatewayMessage> header={translate('order.status')} width="5%">
                    {(rfq) => <OfferStatusTag state={rfq.state} variant="supplier" kycRequested={rfq.kycRequested} />}
                </TableBuilderColumn>
            </TableBuilder>

            <CenteredPagination
                pageTotal={rfqPaginationQuery.pageTotal}
                currentPage={filters.page}
                onPageChange={onPageChange}
            />
        </div>
    );
};
